import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, softwareList } from '@/mocks/data';

const softwareImages: Record<number, string> = {
  1: 'https://readdy.ai/api/search-image?query=Download%20manager%20software%20interface%20design%20concept%2C%20clean%20modern%20UI%20with%20progress%20bars%2C%20warm%20orange%20accents%2C%20minimalist%20tech%20illustration%20on%20light%20neutral%20background&width=400&height=300&seq=sw-001&orientation=landscape',
  2: 'https://readdy.ai/api/search-image?query=System%20optimization%20cleaner%20software%20concept%2C%20clean%20modern%20interface%20design%2C%20warm%20orange%20accents%2C%20minimalist%20tech%20illustration%20on%20light%20background&width=400&height=300&seq=sw-002&orientation=landscape',
  3: 'https://readdy.ai/api/search-image?query=Office%20productivity%20suite%20software%20concept%2C%20clean%20modern%20document%20interface%2C%20warm%20orange%20and%20teal%20accents%2C%20minimalist%20illustration%20on%20light%20background&width=400&height=300&seq=sw-003&orientation=landscape',
  4: 'https://readdy.ai/api/search-image?query=Video%20editing%20software%20timeline%20interface%20concept%2C%20clean%20modern%20design%20with%20film%20elements%2C%20warm%20orange%20accents%2C%20minimalist%20illustration%20on%20light%20background&width=400&height=300&seq=sw-004&orientation=landscape',
  5: 'https://readdy.ai/api/search-image?query=Mobile%20phone%20assistant%20software%20concept%2C%20smartphone%20connected%20to%20computer%2C%20clean%20modern%20interface%2C%20warm%20orange%20accents%2C%20minimalist%20illustration%20on%20light%20background&width=400&height=300&seq=sw-005&orientation=landscape',
  6: 'https://readdy.ai/api/search-image?query=Antivirus%20security%20shield%20software%20concept%2C%20clean%20modern%20protection%20interface%2C%20warm%20orange%20and%20teal%20accents%2C%20minimalist%20illustration%20on%20light%20background&width=400&height=300&seq=sw-006&orientation=landscape',
  7: 'https://readdy.ai/api/search-image?query=Web%20browser%20software%20interface%20concept%2C%20clean%20modern%20design%20with%20speed%20elements%2C%20warm%20orange%20accents%2C%20minimalist%20illustration%20on%20light%20background&width=400&height=300&seq=sw-007&orientation=landscape',
  8: 'https://readdy.ai/api/search-image?query=PDF%20editor%20software%20concept%20with%20document%20editing%20interface%2C%20clean%20modern%20design%2C%20warm%20orange%20accents%2C%20minimalist%20illustration%20on%20light%20background&width=400&height=300&seq=sw-008&orientation=landscape',
  9: 'https://readdy.ai/api/search-image?query=Programming%20IDE%20code%20editor%20software%20concept%2C%20clean%20modern%20dark%20interface%20with%20code%20syntax%2C%20warm%20orange%20accents%2C%20minimalist%20illustration&width=400&height=300&seq=sw-009&orientation=landscape',
  10: 'https://readdy.ai/api/search-image?query=Screen%20recording%20software%20concept%2C%20clean%20modern%20interface%20with%20record%20button%2C%20warm%20orange%20accents%2C%20minimalist%20tech%20illustration%20on%20light%20background&width=400&height=300&seq=sw-010&orientation=landscape',
  11: 'https://readdy.ai/api/search-image?query=Data%20recovery%20software%20concept%2C%20hard%20drive%20scanning%20visualization%2C%20clean%20modern%20design%2C%20warm%20orange%20accents%2C%20minimalist%20illustration%20on%20light%20background&width=400&height=300&seq=sw-011&orientation=landscape',
  12: 'https://readdy.ai/api/search-image?query=Game%20booster%20accelerator%20software%20concept%2C%20clean%20modern%20design%20with%20speed%20and%20gaming%20elements%2C%20warm%20orange%20accents%2C%20minimalist%20illustration%20on%20light%20background&width=400&height=300&seq=sw-012&orientation=landscape',
};

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const [activeCategory, setActiveCategory] = useState(slug || 'system-tools');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const filteredSoftware = softwareList.filter((s) => s.category === activeCategory);

  const allCategorySlugs = categories.map((c) => c.slug);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '软件分类', path: '/category/system-tools' },
              ...(currentCategory ? [{ label: currentCategory.name }] : []),
            ]}
          />

          <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading mb-2">
            软件分类
          </h1>
          <p className="text-foreground-600 mb-10">选择分类浏览您需要的软件工具</p>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveCategory(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Software List */}
          {currentCategory && (
            <>
              <div className="bg-background-100 rounded-xl p-5 mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className={`${currentCategory.icon} text-lg text-accent-600`}></i>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground-900">{currentCategory.name}</h2>
                  <p className="text-sm text-foreground-600">{currentCategory.desc}</p>
                </div>
                <span className="sm:ml-auto text-sm text-foreground-500 whitespace-nowrap">
                  共 {filteredSoftware.length} 款软件
                </span>
              </div>

              {filteredSoftware.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredSoftware.map((sw) => (
                    <div
                      key={sw.id}
                      className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-200 group"
                    >
                      <div className="aspect-[4/3] overflow-hidden bg-background-100">
                        <img
                          src={softwareImages[sw.id]}
                          alt={sw.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-foreground-900 mb-1">{sw.name}</h3>
                            <span className="text-xs text-foreground-500">{sw.version} · {sw.size}</span>
                          </div>
                          <span className="flex items-center gap-1 text-xs font-medium text-accent-600 bg-accent-50 px-2 py-1 rounded-md whitespace-nowrap">
                            <i className="ri-star-fill text-xs"></i>
                            {sw.rating}
                          </span>
                        </div>
                        <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-2">
                          {sw.desc}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-foreground-500">
                            <i className="ri-download-line mr-1"></i>
                            {sw.downloads} 次下载
                          </span>
                          <a
                            href="/"
                            className="flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-download-cloud-2-line"></i>
                            下载
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-600">该分类暂无软件</p>
                </div>
              )}
            </>
          )}

          {!currentCategory && (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-600 mb-4">未找到该分类</p>
              <Link
                to="/category/system-tools"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
              >
                返回分类列表
              </Link>
            </div>
          )}
        </div>

        {/* Schema.org BreadcrumbList */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.kaiyunxiazai.com/' },
                { '@type': 'ListItem', position: 2, name: '软件分类', item: 'https://www.kaiyunxiazai.com/category/system-tools' },
                ...(currentCategory ? [{ '@type': 'ListItem', position: 3, name: currentCategory.name }] : []),
              ],
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}